import { useState, type FormEvent } from 'react';
import { changePasswordRequest } from '@redirector/shared';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';

export type ChangePasswordScreenProps = {
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  forced: boolean;
};

const INPUT_CLASS =
  'rounded-(--radius-md) border border-rule-strong bg-surface-raised px-(--space-3) py-(--space-2) text-ink';

export function ChangePasswordScreen({ changePassword, forced }: ChangePasswordScreenProps) {
  const [fields, setFields] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (fields.newPassword !== fields.confirm) {
      setErrors({ confirm: 'The two passwords do not match' });
      return;
    }

    const parsed = changePasswordRequest.safeParse({
      currentPassword: fields.currentPassword,
      newPassword: fields.newPassword,
    });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        next[String(issue.path[0] ?? 'root')] ??= issue.message;
      }
      setErrors(next);
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      await changePassword(parsed.data.currentPassword, parsed.data.newPassword);
    } catch (error) {
      const fieldErrors =
        error !== null && typeof error === 'object' && 'fields' in error
          ? (error as { fields: Record<string, string> }).fields
          : {};
      setErrors(
        Object.keys(fieldErrors).length > 0
          ? fieldErrors
          : { root: error instanceof Error ? error.message : 'Could not change the password' },
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-[28rem] flex-col justify-center gap-(--space-5) px-(--space-4)">
      <header>
        <h1 className="text-[length:var(--text-title)]">
          {forced ? 'Set a new password' : 'Change your password'}
        </h1>
        {forced && (
          <p className="mt-(--space-2) text-ink-muted">
            This account was created from the deployment configuration. Choose your own password
            before continuing, so the value in the deployment secret stops being a working login.
          </p>
        )}
      </header>

      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}

      <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
        <Field label="Current password" error={errors.currentPassword}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="password"
              autoComplete="current-password"
              autoFocus
              className={INPUT_CLASS}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={fields.currentPassword}
              onChange={(event) =>
                setFields((prev) => ({ ...prev, currentPassword: event.target.value }))
              }
            />
          )}
        </Field>

        <Field
          label="New password"
          hint="At least 12 characters. A memorable phrase beats a short scramble."
          error={errors.newPassword}
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              className={INPUT_CLASS}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={fields.newPassword}
              onChange={(event) =>
                setFields((prev) => ({ ...prev, newPassword: event.target.value }))
              }
            />
          )}
        </Field>

        <Field label="Confirm new password" error={errors.confirm}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              className={INPUT_CLASS}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={fields.confirm}
              onChange={(event) => setFields((prev) => ({ ...prev, confirm: event.target.value }))}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save password'}
        </Button>
      </form>
    </main>
  );
}
